import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Eye, EyeSlash } from "@phosphor-icons/react";
import { toast } from "sonner";
import Brand from "@/components/Brand";

export default function LoginPage() {
  const navigate = useNavigate();
  const { login, formatApiError } = useAuth();
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [showPwd, setShowPwd] = useState(false);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState("");

  const onSubmit = async (e) => {
    e.preventDefault();
    setError("");
    setLoading(true);
    try {
      await login(email, password);
      toast.success("Welcome back");
      navigate("/dashboard");
    } catch (err) {
      setError(formatApiError(err.response?.data?.detail) || err.message);
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="min-h-screen grid lg:grid-cols-2 bg-white">
      {/* Form side */}
      <div className="flex flex-col px-6 sm:px-12 lg:px-20 py-10 justify-center">
        <div className="max-w-md w-full mx-auto">
          <div className="mb-12 flex justify-start">
            <Brand size="md" color="dark" />
          </div>

          <h1 className="font-display text-3xl sm:text-4xl font-bold tracking-tight text-slate-900">
            Sign in
          </h1>

          <form onSubmit={onSubmit} className="mt-10 space-y-5" data-testid="login-form">
            <div className="space-y-1.5">
              <Label htmlFor="email" className="text-xs font-semibold uppercase tracking-widest text-slate-500">Email</Label>
              <Input
                id="email"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder="you@business.com"
                required
                data-testid="login-email-input"
                className="h-11"
              />
            </div>
            <div className="space-y-1.5">
              <Label htmlFor="password" className="text-xs font-semibold uppercase tracking-widest text-slate-500">Password</Label>
              <div className="relative">
                <Input
                  id="password"
                  type={showPwd ? "text" : "password"}
                  value={password}
                  onChange={(e) => setPassword(e.target.value)}
                  placeholder="••••••••"
                  required
                  data-testid="login-password-input"
                  className="h-11 pr-10"
                />
                <button
                  type="button"
                  onClick={() => setShowPwd((s) => !s)}
                  className="absolute right-3 top-1/2 -translate-y-1/2 text-slate-400 hover:text-slate-600"
                  aria-label="Toggle password visibility"
                  data-testid="login-toggle-password"
                >
                  {showPwd ? <EyeSlash size={18} /> : <Eye size={18} />}
                </button>
              </div>
            </div>

            {error && (
              <div className="text-sm text-[#D92D20] bg-red-50 border border-red-100 rounded-md px-3 py-2" data-testid="login-error">
                {error}
              </div>
            )}

            <Button
              type="submit"
              disabled={loading}
              className="w-full h-11 bg-[#327de3] hover:bg-[#1f64c2] text-white font-medium rounded-lg"
              data-testid="login-submit-button"
            >
              {loading ? "Signing in…" : "Sign in"}
            </Button>
          </form>

          <p className="mt-8 text-sm text-slate-500">
            Strata is invite-only. If you've received an invite, check your email for the link to join.
          </p>
        </div>
      </div>

      {/* Image side */}
      <div className="hidden lg:block relative overflow-hidden bg-[#1f64c2]">
        <img
          src="https://images.unsplash.com/photo-1696029475209-b759293ed2b0?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA1NzR8MHwxfHNlYXJjaHwzfHxhYnN0cmFjdCUyMGJsdWUlMjBhcmNoaXRlY3R1cmUlMjBnbGFzc3xlbnwwfHx8fDE3Nzc5NDUyMzN8MA&ixlib=rb-4.1.0&q=85"
          alt="Banking architecture"
          className="absolute inset-0 w-full h-full object-cover opacity-90"
        />
        <div className="absolute inset-0 bg-gradient-to-tr from-[#173f8a]/80 via-[#1f64c2]/30 to-transparent" />
        <div className="relative h-full flex flex-col justify-end p-12 text-white">
          <h2 className="font-display text-6xl font-bold tracking-tight max-w-md">
            Strata
          </h2>
          <p className="mt-2 text-lg font-semibold text-white/90 max-w-md tracking-wide">
            By Friedman Group
          </p>
        </div>
      </div>
    </div>
  );
}
